import { useEffect, useMemo, useState } from "react";
import type { FormField } from "../../components/AdminDataModal";
import type { Column } from "../../components/DataTable";
import { useMessageModal } from "../../contexts/MessageModalContext";
import { useFormat } from "../../../context/FormatContext";
import DataTable from "../../components/DataTable";
import AdminListToolkit from "../../components/listing/AdminListToolkit";
import AdminDataModal from "../../components/AdminDataModal";

export default function AdminDiscountVouchers() {
  const BASE = import.meta.env.VITE_LYRAZONE_BASE_URL;
  const { showModal } = useMessageModal();
  const { formatDateOnly } = useFormat();

  const [data, setData] = useState<any[]>([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit" | "view">("add");
  const [activeRow, setActiveRow] = useState<any>(null);
  const [products, setProducts] = useState<any[]>([]);

  const formatCurrency = (value: any) => {
    const amount = Number(value || 0);

    return amount.toLocaleString("en-MY", {
      style: "currency",
      currency: "MYR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatDateForInput = (value: any) => {
    if (!value) return "";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    return date.toISOString().split("T")[0];
  };

  const normalizeVoucherPayload = (formData: any) => {
    return {
      ...formData,

      voucher_type: formData.voucher_type || "discount",

      discount_type:
        formData.voucher_type === "discount"
          ? formData.discount_type || "fixed"
          : "fixed",

      discount_value:
        formData.voucher_type === "discount"
          ? String(formData.discount_value || "").replace(/,/g, "")
          : 0,

      min_product_quantity: Number(formData.min_product_quantity || 1),

      min_product_amount: String(formData.min_product_amount || "0").replace(
        /,/g,
        "",
      ),

      freeGiftProductIds:
        formData.voucher_type === "free_gift"
          ? JSON.stringify(formData.freeGiftProductIds || [])
          : JSON.stringify([]),

      free_gift_quantity:
        formData.voucher_type === "free_gift"
          ? Number(formData.free_gift_quantity || 1)
          : 1,

      buy_quantity:
        formData.voucher_type === "buy_one_get_one"
          ? Number(formData.buy_quantity || 1)
          : 1,

      free_quantity:
        formData.voucher_type === "buy_one_get_one"
          ? Number(formData.free_quantity || 1)
          : 1,

      max_discount_amount:
        formData.max_discount_amount === "" ||
        formData.max_discount_amount === null ||
        formData.max_discount_amount === undefined
          ? null
          : String(formData.max_discount_amount).replace(/,/g, ""),

      usage_limit:
        formData.usage_limit === "" ||
        formData.usage_limit === null ||
        formData.usage_limit === undefined
          ? null
          : Number(formData.usage_limit),

      per_user_limit:
        formData.per_user_limit === "" ||
        formData.per_user_limit === null ||
        formData.per_user_limit === undefined
          ? null
          : Number(formData.per_user_limit),

      start_date: formData.start_date || null,
      end_date: formData.end_date || null,
    };
  };

  const fetchDiscountVouchers = async () => {
    try {
      const res = await fetch(`${BASE}/admin/discount-vouchers`, {
        credentials: "include",
      });

      const result = await res.json();
      const freshData = result.data || [];

      if (result.success) {
        setData(freshData);

        setSelectedRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );

        setActiveRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );
      }
    } catch (error) {
      console.error("Fetch Discount Vouchers Error:", error);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await fetch(`${BASE}/admin/products`, {
        credentials: "include",
      });

      const result = await res.json();

      if (result.success) {
        setProducts(result.data || []);
      }
    } catch (error) {
      console.error("Fetch Products Error:", error);
    }
  };

  useEffect(() => {
    fetchDiscountVouchers();
    fetchProducts();
  }, []);

  const handleSubmit = async (formData: any) => {
    try {
      const method = modalMode === "add" ? "POST" : "PUT";

      const url =
        modalMode === "add"
          ? `${BASE}/admin/discount-vouchers`
          : `${BASE}/admin/discount-vouchers/${activeRow.id}`;

      const payload = normalizeVoucherPayload(formData);

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const result = await res.json();

      if (result.success) {
        fetchDiscountVouchers();
        setModalOpen(false);
        setActiveRow(null);

        showModal({
          type: "success",
          title: "Success",
          message:
            modalMode === "add"
              ? "Discount voucher added successfully."
              : "Discount voucher updated successfully.",
        });
      } else {
        showModal({
          type: "error",
          title: "Error",
          message: result.message || "Unable to save discount voucher.",
        });
      }
    } catch (error) {
      console.error("Submit Discount Voucher Error:", error);

      showModal({
        type: "error",
        title: "Error",
        message: "Something went wrong while saving discount voucher.",
      });
    }
  };

  const handleDelete = (row: any) => {
    showModal({
      type: "confirm",
      title: "Delete Discount Voucher",
      message: "Are you sure you want to delete this discount voucher?",
      confirmText: "Delete",
      onConfirm: async () => {
        try {
          const res = await fetch(`${BASE}/admin/discount-vouchers/${row.id}`, {
            method: "DELETE",
            credentials: "include",
          });

          const result = await res.json();

          if (result.success) {
            fetchDiscountVouchers();
            setSelectedRow(null);

            showModal({
              type: "success",
              title: "Deleted",
              message: "Discount voucher deleted successfully.",
            });
          } else {
            showModal({
              type: "error",
              title: "Error",
              message: result.message || "Unable to delete discount voucher.",
            });
          }
        } catch (error) {
          console.error("Delete Discount Voucher Error:", error);

          showModal({
            type: "error",
            title: "Error",
            message: "Something went wrong while deleting discount voucher.",
          });
        }
      },
    });
  };

  const voucherFields: FormField[] = useMemo(
    () => [
      { key: "id", label: "ID", colSpan: 2 },
      { key: "title", label: "Voucher Title", colSpan: 2 },
      { key: "code", label: "Voucher Code", colSpan: 2 },
      {
        key: "description",
        label: "Description",
        type: "textarea",
        colSpan: 2,
      },
      {
        key: "voucher_type",
        label: "Voucher Type",
        type: "select",
        options: [
          { label: "Discount", value: "discount" },
          { label: "Free Gift Items", value: "free_gift" },
          { label: "Buy One Get One Free", value: "buy_one_get_one" },
        ],
        colSpan: 2,
      },

      {
        key: "discount_type",
        label: "Discount Type",
        type: "select",
        options: [
          { label: "Fixed Amount", value: "fixed" },
          { label: "Percentage", value: "percent" },
        ],
        showWhen: {
          key: "voucher_type",
          value: "discount",
        },
        colSpan: 2,
      },
      {
        key: "discount_value",
        label: "Discount Value",
        type: "currency",
        showWhen: {
          key: "voucher_type",
          value: "discount",
        },
        colSpan: 2,
      },

      {
        key: "min_product_quantity",
        label: "Minimum Product Quantity",
        type: "number",
        colSpan: 2,
      },
      {
        key: "min_product_amount",
        label: "Minimum Product Amount",
        type: "currency",
        colSpan: 2,
      },

      {
        key: "freeGiftProductIds",
        label: "Free Gift Items",
        type: "multiselect",
        options: products.map((product) => ({
          label: `${product.productName} - SKU: ${product.SKU || "-"}`,
          value: product.id,
        })),
        showWhen: {
          key: "voucher_type",
          value: "free_gift",
        },
        colSpan: 2,
      },
      {
        key: "free_gift_quantity",
        label: "Free Gift Quantity Per Item",
        type: "number",
        showWhen: {
          key: "voucher_type",
          value: "free_gift",
        },
        colSpan: 2,
      },

      {
        key: "buy_quantity",
        label: "Buy Quantity",
        type: "number",
        showWhen: {
          key: "voucher_type",
          value: "buy_one_get_one",
        },
        colSpan: 2,
      },
      {
        key: "free_quantity",
        label: "Free Quantity",
        type: "number",
        showWhen: {
          key: "voucher_type",
          value: "buy_one_get_one",
        },
        colSpan: 2,
      },

      {
        key: "max_discount_amount",
        label: "Maximum Discount Amount",
        type: "currency",
        showWhen: {
          key: "voucher_type",
          value: "discount",
        },
        colSpan: 2,
      },
      {
        key: "usage_limit",
        label: "Total Usage Limit",
        type: "number",
        colSpan: 2,
      },
      {
        key: "per_user_limit",
        label: "Per User Limit",
        type: "number",
        colSpan: 2,
      },
      {
        key: "start_date",
        label: "Start Date",
        type: "date",
        colSpan: 2,
      },
      {
        key: "end_date",
        label: "End Date",
        type: "date",
        colSpan: 2,
      },
      {
        key: "status",
        label: "Status",
        type: "select",
        options: [
          { label: "Active", value: "active" },
          { label: "Inactive", value: "inactive" },
        ],
        colSpan: 2,
      },
    ],
    [products],
  );

  const columns: Column[] = [
    { key: "id", label: "ID" },
    { key: "title", label: "Title" },
    { key: "code", label: "Code" },
    {
      key: "discount_type",
      label: "Type",
      render: (row: any) =>
        row.discount_type === "percent" ? "Percentage" : "Fixed Amount",
    },
    {
      key: "voucher_type",
      label: "Voucher Type",
      render: (row: any) => {
        if (row.voucher_type === "free_gift") return "Free Gift";
        if (row.voucher_type === "buy_one_get_one") return "Buy One Get One";
        return "Discount";
      },
    },
    {
      key: "discount_value",
      label: "Benefit",
      render: (row: any) => {
        if (row.voucher_type === "free_gift") {
          const gifts = row.freeGiftProducts?.length
            ? row.freeGiftProducts
                .map((product: any) => product.productName)
                .join(", ")
            : "Gift Items";

          return `${gifts} x ${row.free_gift_quantity || 1}`;
        }

        if (row.voucher_type === "buy_one_get_one") {
          return `Buy ${row.buy_quantity || 1} Get ${row.free_quantity || 1}`;
        }

        return row.discount_type === "percent"
          ? `${Number(row.discount_value || 0)}%`
          : formatCurrency(row.discount_value);
      },
    },
    {
      key: "min_product_quantity",
      label: "Min Qty",
      render: (row: any) => row.min_product_quantity || 1,
    },
    {
      key: "min_product_amount",
      label: "Min Amount",
      render: (row: any) => formatCurrency(row.min_product_amount),
    },
    {
      key: "start_date",
      label: "Start Date",
      render: (row: any) => formatDateOnly(row.start_date),
    },
    {
      key: "end_date",
      label: "End Date",
      render: (row: any) => formatDateOnly(row.end_date),
    },
    {
      key: "status",
      label: "Status",
      render: (row: any) => (
        <span
          className={row.status === "active" ? "badge-success" : "badge-danger"}
        >
          {row.status?.charAt(0).toUpperCase() + row.status?.slice(1)}
        </span>
      ),
    },
  ];

  return (
    <div>
      <AdminListToolkit
        title="Discount Vouchers"
        columns={columns}
        data={data}
        selectedRow={selectedRow}
        onAdd={() => {
          const newRow = {
            id: "",
            title: "",
            code: "",
            description: "",
            voucher_type: "discount",
            discount_type: "fixed",
            discount_value: "",
            min_product_quantity: 1,
            min_product_amount: "0",
            freeGiftProductIds: [],
            free_gift_quantity: 1,
            buy_quantity: 1,
            free_quantity: 1,
            max_discount_amount: "",
            usage_limit: "",
            per_user_limit: "",
            start_date: "",
            end_date: "",
            status: "active",
          };
          setModalMode("add");
          setActiveRow(newRow);
          setModalOpen(true);
        }}
        onView={(row) => {
          setModalMode("view");
          setActiveRow({
            ...row,
            freeGiftProductIds: row.freeGiftProductIds || [],
            start_date: formatDateForInput(row.start_date),
            end_date: formatDateForInput(row.end_date),
          });
          setModalOpen(true);
        }}
        onEdit={(row) => {
          setModalMode("edit");
          setActiveRow({
            ...row,
            freeGiftProductIds: row.freeGiftProductIds || [],
            start_date: formatDateForInput(row.start_date),
            end_date: formatDateForInput(row.end_date),
          });
          setModalOpen(true);
        }}
        onSoftDelete={handleDelete}
        enableExport={false}
      >
        {(filteredData) => (
          <DataTable
            columns={columns}
            data={filteredData}
            selectedRowId={selectedRow?.id}
            onRowSelect={setSelectedRow}
          />
        )}
      </AdminListToolkit>

      <AdminDataModal
        isOpen={modalOpen}
        mode={modalMode}
        title="Discount Voucher"
        fields={voucherFields}
        rowData={activeRow}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
        size="xl"
      />
    </div>
  );
}
